import {
  ChangeDetectorRef,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Inject,
  input,
  OnChanges,
  Output,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import {DatePipe} from '@angular/common';

import {DATE_FORMAT, STAFF_MEMBER_DEFAULT_FILTER, TIME_FORMAT} from '../../../api/constants';
import {Exam} from '../../../api/models/exam';
import {Survey} from '../../../api/models/survey';
import {User} from '../../../api/models/user';

import {SurveyService} from '../../../api/services/survey.service';

import {
  ConfirmationDialogComponent
} from '../../general-purpose/dialogs/confirmation-dialog/confirmation-dialog.component';
import {
  WaButtonDirective,
  WaCalloutDirective,
  WaDividerDirective,
  WaIconDirective,
  WaInputDirective,
  WaProgressBarDirective,
  WaScrollerDirective,
  WaSpinnerDirective,
  WaToastService
} from "angular-awesome";
import {Dialog} from "@angular/cdk/dialog";
import {rxResource, toSignal} from "@angular/core/rxjs-interop";
import {FormControl, ReactiveFormsModule,} from "@angular/forms";
import {debounceTime, lastValueFrom} from "rxjs";
import {StaffMember, StaffMemberFilter} from "../../../api/models/staff-member";
import {StaffMemberService} from "../../../api/services/staff-member.service";
import {StaffmemberListElementComponent} from "./staffmember-list-element/staffmember-list-element.component";
import {StaffMemberAssignmentsData} from "../../../api/models/exam-assignments";
import {
  StaffmemberAssignmentsDialogComponent
} from "../staffmember-assignments-dialog/staffmember-assignments-dialog.component";

const LIMIT = 500 // Max number of exams to fetch per request

@Component({
  selector: 'app-staffmember-list',
  templateUrl: './staffmember-list.component.html',
  styleUrls: ['./staffmember-list.component.scss'],
  imports: [WaButtonDirective, WaIconDirective, WaScrollerDirective, WaCalloutDirective, ReactiveFormsModule, WaInputDirective, WaDividerDirective, WaSpinnerDirective, StaffmemberListElementComponent, WaProgressBarDirective],
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class StaffMemberListComponent implements OnChanges {

  readonly user = input<User | null>(null);
  readonly survey = input.required<Survey>();

  defaultFilter = STAFF_MEMBER_DEFAULT_FILTER;

  filter: StaffMemberFilter = this.defaultFilter;
  lastNameFilter = new FormControl();

  lastName = toSignal(this.lastNameFilter.valueChanges.pipe(debounceTime(500)));

  staffmember_params = computed(() => ({
    survey_id: this.survey().id, skip: 0, limit: 100, filter: {
      ...this.filter, lastName: this.lastName()?.length >= 2 ? this.lastName() : null,
    }
  }));
  staffmembers = rxResource({
    params: this.staffmember_params,
    stream: ({params}) => this.surveyService.getStaffMembers(params.survey_id, params.skip, params.limit, params.filter)
  });

  // StaffMambersView as a readonly view sorted by last name, first name
  staffMembersView = computed<StaffMember[]>(() => this.staffmembers.hasValue() ? this.staffmembers.value().data.toSorted((a, b) => {
    const lastNameCompare = a.last_name.localeCompare(b.last_name);
    if (lastNameCompare === 0) {
      return a.first_name.localeCompare(b.first_name);
    }
    return lastNameCompare;
  }) : []);

  //selectedExam: Exam | null = null;
  totalCount: number = 0;
  // expose loading flag as a signal so template can show a spinner while data is prepared
  isLoading = signal(false);


  @Output() selectedEvent = new EventEmitter<Exam | null>();

  constructor(private dialog: Dialog, private datePipe: DatePipe, private staffmemberservice: StaffMemberService, private surveyService: SurveyService, private toastService: WaToastService, private cd: ChangeDetectorRef, @Inject(DATE_FORMAT) public dateFormat: string, @Inject(TIME_FORMAT) public timeFormat: string) {
  }

  async displayAssignments(staffmember: StaffMember): Promise<void> {
    // show loading indicator while preparing data for the dialog
    this.isLoading.set(true);
    // load supervising / correcting lists (must finish before opening dialog)
    const surveyId = this.survey().id;
    const staffMemberId = staffmember.id;

    // load correcting / suggested / excluded lists (must finish before opening dialog)
    let fixedSupervising: Exam[] = [];
    let suggestedSupervising: Exam[] = [];
    let excludedSupervising: Exam[] = [];
    let fixedCorrecting: Exam[] = [];
    let suggestedCorrecting: Exam[] = [];
    let excludedCorrecting: Exam[] = [];

    try {
      // find exams where this staff member is assigned for supervision or correction
      const supervising = await lastValueFrom(this.surveyService.getSupervisions(surveyId, staffMemberId));
      fixedSupervising = supervising.data.map((item: any) => item.exam);
      const correcting = await lastValueFrom(this.surveyService.getCorrections(surveyId, staffMemberId));
      fixedCorrecting = correcting.data.map((item: any) => item.exam);

      // Find exams where this staff member is suggested for supervising or correcting
      const suggestedSup  = await lastValueFrom(this.surveyService.getSupervisionsSuggested(surveyId, staffMemberId));
      suggestedSupervising = suggestedSup.data.map((item: any) => item.exam);
      const suggestedCorr = await lastValueFrom(this.surveyService.getCorrectionsSuggested(surveyId, staffMemberId));
      suggestedCorrecting = suggestedCorr.data.map((item: any) => item.exam);

      // find exams where this staff member is exempt from supervising or correcting
      const excludedSup  = await lastValueFrom(this.surveyService.getSupervisionsExempt(surveyId, staffMemberId));
      excludedSupervising = excludedSup.data.map((item: any) => item.exam);
      const excludedCorr = await lastValueFrom(this.surveyService.getCorrectionsExempt(surveyId, staffMemberId));
      excludedCorrecting = excludedCorr.data.map((item: any) => item.exam);
    } catch (error: any) {
      // abort: show toast, release lock and stop
      this.toastService.danger(`Error obtaining supervision or corrections for staff member ${staffMemberId}. Aborting.`);
      this.isLoading.set(false);
      return;
    }

    // Hide preparatory overlay before opening the dialog to avoid it covering the CDK overlay
    this.isLoading.set(false);
    // ensure view updates
    this.cd.detectChanges();

    // Open dialog in next macrotask so the browser can repaint and remove the overlay before the CDK overlay attaches
    setTimeout(() => {
      const ref = this.dialog.open<StaffMemberAssignmentsData>(StaffmemberAssignmentsDialogComponent, {
        disableClose: true, data: {
          survey: this.survey(), staffMember: staffmember,
          fixedSupervising: fixedSupervising,
          fixedCorrecting: fixedCorrecting,
          suggestedSupervising: suggestedSupervising,
          suggestedCorrecting: suggestedCorrecting,
          excludedSupervising: excludedSupervising,
          excludedCorrecting: excludedCorrecting,
        },
      });

      // Also ensure we clear loading flag when dialog closes (defensive)
      ref.closed.subscribe(() => {
        this.isLoading.set(false);
      });
      // Defensive: ensure overlay DOM is removed in case timing/z-index left it visible
      this.isLoading.set(false);
      this.cd.detectChanges();
      setTimeout(() => {
        try {
          document.querySelectorAll('.loading-overlay').forEach(el => el.remove());
        } catch (e) {
          // ignore
        }
      }, 0);
    }, 0);
  }

  editSupervisions(staffmember: StaffMember): void {
  }

  editCorrections(staffmember: StaffMember): void {
  }

  //  this.selectedExam = exam;
  //  this.selectedEvent.emit(exam);
  // }

  editStaffMember(staffmember: StaffMember): void {
    // TODO: Future add-on
  }

  removeStaffMember(staffmember: StaffMember): void {
    const staffmemberName = staffmember.last_name + ", " + staffmember.first_name;
    const ref = this.dialog.open(ConfirmationDialogComponent, {
      disableClose: true, data: {
        title: 'Confirm deletion',
        message: `Are you sure that you want to delete the staff member "{staffmemberName}"?`,
      }
    });

    ref.closed.subscribe(data => {
      if (!data) return;
      this.staffmemberservice.delete(staffmember.id).subscribe({
        next: () => {
          this.staffmembers.update((items) => items ? {
            count: items.count - 1, data: items.data.filter(elt => elt !== staffmember),
          } : undefined);
          this.toastService.success(`Staff Member ${staffmemberName} has been deleted.`);
        }, error: err => {
          this.toastService.danger(`Could not delete exam ${staffmemberName}.`);
        }
      });
    });
  }

  showFilterDialog(): void {
    // const ref = this.dialog.open<StaffMemberFilter>(StaffMemberFilterComponent, {
    //   disableClose: true,
    //   data: {filter: this.filter}
    // });
    //
    // ref.closed.subscribe(filter => {
    //   if (!filter) return;
    //   this.filter = filter;
    //   this.staffmembers.reload();
    // });
  }

  resetFilter(): void {
    this.filter = this.defaultFilter;
    this.lastNameFilter.setValue('');
    this.staffmembers.reload();
  }

  showCreateDialog(): void {
    // TODO: Future add-on
  }

  //ngOnInit(): void {
  //  this.getExams();
  // }

  ngOnChanges(): void {
    const user = this.user();
    const survey = this.survey();

    if (user && survey) {
      this.staffmembers.reload();
      return;
    }

    this.totalCount = this.staffmembers.hasValue() ? this.staffmembers.value().count : 0;
    //this.selectedExam = null;
  }
}
